
import React from 'react';
import { SaleDetails } from "@/types/sales";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { calculateCommission, formatCurrency, getCommissionRate } from "@/utils/sales/commissionCalculations";
import { calculatePolicyTotal } from "@/utils/sales/policyTotalCalculation";

interface EstimatedCommissionsProps {
  salesMetrics: {
    monthlyPolicies: SaleDetails[];
  } | undefined;
}

interface CarrierCommission {
  name: string;
  policies: SaleDetails[];
  totalPremium: number;
  commissionRate: number;
  commissionAmount: number;
}

export const EstimatedCommissions = ({ salesMetrics }: EstimatedCommissionsProps) => {
  // Group policies by carrier
  const groupedByCarrier = salesMetrics?.monthlyPolicies?.reduce((acc, policy) => {
    const carrier = policy.insurance_carrier || 'Unknown';
    if (!acc[carrier]) {
      acc[carrier] = [];
    }
    acc[carrier].push(policy);
    return acc;
  }, {} as Record<string, SaleDetails[]>);

  // Calculate commission data for each carrier using the correct premium calculation
  const commissionData: CarrierCommission[] = React.useMemo(() => {
    if (!groupedByCarrier) return [];

    return Object.entries(groupedByCarrier)
      .map(([carrier, policies]) => {
        // Only use the premium_amount as it already includes endorsement effects
        const totalPremium = policies.reduce((sum, policy) => sum + (Number(policy.premium_amount) || 0), 0);
        const commissionRate = getCommissionRate(carrier);
        const commissionAmount = totalPremium * commissionRate;

        return {
          name: carrier,
          policies,
          totalPremium,
          commissionRate,
          commissionAmount
        };
      })
      // Sort by commission amount (highest first)
      .sort((a, b) => b.commissionAmount - a.commissionAmount);
  }, [groupedByCarrier]);

  // Calculate grand totals
  const totals = React.useMemo(() => {
    return commissionData.reduce(
      (acc, curr) => ({
        policies: acc.policies + curr.policies.length,
        premium: acc.premium + curr.totalPremium,
        commission: acc.commission + curr.commissionAmount
      }),
      { policies: 0, premium: 0, commission: 0 }
    );
  }, [commissionData]);

  return (
    <Card className="bg-gradient-to-br from-white via-[#F7F5FF] to-[#E5DEFF]/80 hover:shadow-lg border-purple-100">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-lg font-medium">
          Estimated Commissions
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Carrier</TableHead>
                <TableHead className="text-right">Policies</TableHead>
                <TableHead className="text-right">Premium</TableHead>
                <TableHead className="text-right">Rate</TableHead>
                <TableHead className="text-right">Commission</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {commissionData.map((data) => (
                <TableRow key={data.name}>
                  <TableCell className="font-medium">{data.name}</TableCell>
                  <TableCell className="text-right">{data.policies.length}</TableCell>
                  <TableCell className="text-right">{formatCurrency(data.totalPremium)}</TableCell>
                  <TableCell className="text-right">{(data.commissionRate * 100).toFixed(0)}%</TableCell>
                  <TableCell className="text-right">{formatCurrency(data.commissionAmount)}</TableCell>
                </TableRow>
              ))}
              {commissionData.length > 0 && (
                <TableRow className="bg-purple-50/50 font-semibold">
                  <TableCell>TOTALS</TableCell>
                  <TableCell className="text-right">{totals.policies}</TableCell>
                  <TableCell className="text-right">{formatCurrency(totals.premium)}</TableCell>
                  <TableCell className="text-right">-</TableCell>
                  <TableCell className="text-right">{formatCurrency(totals.commission)}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};
